import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, of, tap, timeout } from 'rxjs';
import { FetchFeatureFlags } from './feature-flags.actions';
import { Logout } from '../auth/auth.actions';
import { FeatureFlagApiService } from '../../core/services/feature-flag/feature-flag-api.service';
import { FeatureFlag } from '../../core/data-models/feature-flag.model';

const FEATURE_FLAGS_FETCH_TIMEOUT_MS = 5 * 1000;

export interface FeatureFlagsStateModel {
  featureFlags: FeatureFlag[];
  loaded: boolean;
  /** Identity the cached flags were fetched for; null when nothing is cached. */
  userId: string | null;
}

@State<FeatureFlagsStateModel>({
  name: 'featureFlags',
  defaults: {
    featureFlags: [],
    loaded: false,
    userId: null
  }
})
@Injectable()
export class FeatureFlagsState {
  constructor(private featureFlagApiService: FeatureFlagApiService) {}

  @Selector()
  static getFeatureFlags(state: FeatureFlagsStateModel): FeatureFlag[] {
    return state.featureFlags;
  }

  @Selector()
  static isLoaded(state: FeatureFlagsStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getUserId(state: FeatureFlagsStateModel): string | null {
    return state.userId;
  }

  @Action(FetchFeatureFlags)
  fetchFeatureFlags(ctx: StateContext<FeatureFlagsStateModel>, { userId }: FetchFeatureFlags) {
    return this.featureFlagApiService.getFeatureFlags().pipe(
      timeout(FEATURE_FLAGS_FETCH_TIMEOUT_MS),
      tap(featureFlags => ctx.patchState({ featureFlags, loaded: true, userId })),
      catchError(error => {
        // Fall back to the last successful fetch, but only for the same user:
        // the state is persisted to localStorage, so a second user on the same
        // browser must not inherit the first user's feature access.
        const cached = ctx.getState();
        if (cached.loaded && cached.userId === userId) {
          console.warn('[FeatureFlagsState] Failed to fetch feature flags, using cached flags:', error);
          return of(cached.featureFlags);
        }
        console.warn('[FeatureFlagsState] Failed to fetch feature flags, defaulting to empty list:', error);
        // Deliberately leaves `loaded` false so an empty set is never cached as
        // a successful fetch. Evaluation stays fail-closed for this session.
        ctx.patchState({ featureFlags: [], loaded: false, userId: null });
        return of([] as FeatureFlag[]);
      })
    );
  }

  /**
   * Clears cached flags on logout so the next login re-fetches fresh values
   * instead of reusing a previous user's flags.
   */
  @Action(Logout)
  resetOnLogout(ctx: StateContext<FeatureFlagsStateModel>) {
    ctx.setState({ featureFlags: [], loaded: false, userId: null });
  }
}
